import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { seoApi } from "@/lib/seoApi";
import { toast } from "sonner";
import { ArrowLeft, Newspaper, ExternalLink, Copy as CopyIcon, Plus } from "lucide-react";

export default function BriefDetails() {
  const { briefId } = useParams();
  const [brief, setBrief] = useState(null);
  const [versions, setVersions] = useState([]);
  const [preview, setPreview] = useState(null);
  const [runs, setRuns] = useState([]);
  const [generating, setGenerating] = useState(false);

  async function load() {
    const [b, v, p, r] = await Promise.all([
      seoApi.get(`/seo/briefs/${briefId}`),
      seoApi.get(`/seo/briefs/${briefId}/versions`),
      seoApi.get(`/seo/briefs/${briefId}/rendered-prompt`),
      seoApi.get(`/seo/workflows/runs?brief_id=${briefId}&limit=50`),
    ]);
    setBrief(b.data); setVersions(v.data); setPreview(p.data); setRuns(r.data);
  }
  useEffect(() => { load(); }, [briefId]);

  async function generate() {
    setGenerating(true);
    try {
      const r = await seoApi.post("/seo/workflows/generate-from-brief", {
        brief_id: briefId, provider_hint: "anthropic",
      });
      toast.success("Generation complete");
      window.location.href = `/seo-studio/runs/${r.data.id}`;
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setGenerating(false);
    }
  }

  if (!brief) return <div className="text-neutral-500">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="brief-details-page">
      <div className="flex items-center gap-3">
        <Link to="/seo-studio/briefs"
              className="text-sm text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Briefs
        </Link>
      </div>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{brief.data.title}</h1>
          <div className="text-sm text-neutral-500 mt-1">
            v{brief.current_version} · created {new Date(brief.created_at).toLocaleString()}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/seo-studio/briefs/new">
            <Button variant="outline" data-testid="brief-details-new">
              <Plus size={14} /> New Brief
            </Button>
          </Link>
          <Link to={`/seo-studio/briefs/${briefId}/edit`}>
            <Button variant="outline" data-testid="brief-details-edit">
              Edit brief
            </Button>
          </Link>
          <Link to={`/seo-studio/briefs/new?copy_from=${briefId}`}>
            <Button variant="outline" data-testid="brief-details-duplicate">
              <CopyIcon size={14} /> Duplicate
            </Button>
          </Link>
          <Button onClick={generate} disabled={generating || !brief.is_active}
                  data-testid="brief-details-generate">
            {generating ? "Generating…" : "Generate via ACF"}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Brief data</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2">
          <Row k="Primary keyword" v={brief.data.primary_keyword} />
          <Row k="Secondary keywords" v={(brief.data.secondary_keywords || []).join(", ") || "—"} />
          <Row k="Search intent" v={brief.data.search_intent} />
          <Row k="Country / Language" v={
            [brief.data.country, brief.data.language].filter(Boolean).join(" · ") || "—"
          } />
          <Row k="Target audience" v={brief.data.target_audience} />
          <Row k="Tone of voice" v={brief.data.tone_of_voice} />
          <Row k="Length" v={`${brief.data.min_words}–${brief.data.max_words} words`} />
          <Row k="Call to action" v={brief.data.cta} />
          <Row k="LLM provider / model" v={
            [brief.data.provider, brief.data.model].filter(Boolean).join(" / ") || "—"
          } />
          <Row k="Required terms" v={(brief.data.required_terms || []).join(", ") || "—"} />
          <Row k="Forbidden terms" v={(brief.data.forbidden_terms || []).join(", ") || "—"} />
          <Row k="Additional instructions" v={brief.data.additional_instructions} />
          <div>
            <div className="text-neutral-500 text-xs uppercase tracking-wide mt-4">Outline</div>
            <ol className="list-decimal ml-5 mt-1 space-y-1">
              {(brief.data.outline || []).map((s, i) => (
                <li key={i}>
                  <span className="text-neutral-500 text-xs mr-1">
                    H{s.level || 2}
                  </span>
                  <span className="font-medium">{s.heading}</span>
                  {s.key_points?.length ? <span className="text-neutral-500"> — {s.key_points.join("; ")}</span> : null}
                </li>
              ))}
            </ol>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Deterministic rendered prompt (sent to ACF §6)</CardTitle></CardHeader>
        <CardContent>
          <pre className="text-xs bg-neutral-50 p-4 rounded border border-neutral-200 overflow-auto whitespace-pre-wrap">
{preview?.final_prompt || ""}
          </pre>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Generation runs for this brief</CardTitle>
          <Link
            to={`/seo-studio/published?brief_id=${briefId}`}
            className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1"
            data-testid="brief-published-link"
          >
            <Newspaper size={12} /> Published articles
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          {runs.length === 0 ? (
            <div className="text-sm text-neutral-500 p-6">No runs yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-neutral-500">
                <tr className="border-t border-neutral-200">
                  <th className="text-left px-6 py-2">Status</th>
                  <th className="text-left px-4 py-2">Provider</th>
                  <th className="text-left px-4 py-2">Brief version</th>
                  <th className="text-left px-4 py-2">Started</th>
                  <th className="text-left px-4 py-2">Published</th>
                  <th className="px-6 py-2" />
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-t border-neutral-100">
                    <td className="px-6 py-3"><Badge variant="outline">{r.status}</Badge></td>
                    <td className="px-4 py-3">{r.provider}/{r.model}</td>
                    <td className="px-4 py-3">v{r.brief_version}</td>
                    <td className="px-4 py-3 text-neutral-500">{new Date(r.started_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      {r.published_id ? (
                        <a
                          href={`/api/cms/admin/collections/blog-posts/${r.published_id}`}
                          target="_blank" rel="noreferrer noopener"
                          className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900"
                          data-testid={`brief-run-cms-${r.id}`}
                        >
                          <ExternalLink size={12} /> {r.published_id.slice(0, 8)}
                        </a>
                      ) : (
                        <span className="text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Link to={`/seo-studio/runs/${r.id}`} className="underline hover:no-underline">View</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Version history</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-xs text-neutral-500">
              <tr className="border-t border-neutral-200">
                <th className="text-left px-6 py-2">Version</th>
                <th className="text-left px-4 py-2">Change reason</th>
                <th className="text-left px-4 py-2">Author</th>
                <th className="text-left px-4 py-2">At</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id} className="border-t border-neutral-100">
                  <td className="px-6 py-3">v{v.version}</td>
                  <td className="px-4 py-3">{v.change_reason}</td>
                  <td className="px-4 py-3">{v.author}</td>
                  <td className="px-4 py-3 text-neutral-500">{new Date(v.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-3 items-baseline">
      <div className="text-neutral-500 text-xs w-40 uppercase tracking-wide">{k}</div>
      <div>{v || "—"}</div>
    </div>
  );
}
